import { PLATFORM_TIME_ZONE } from "@repo/app-types/constants/timeZone";
import {
  AutomationTemplateIds,
  type Automation,
} from "@repo/app-types/entities/automation";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { describeSchedule } from "@repo/ui/scheduling/describeSchedule";
import { nextScheduledRunAt } from "@repo/ui/scheduling/nextScheduledRun";
import { DateTime } from "luxon";

/**
 * Humanizes when an automation runs, for the Trigger column of the automations
 * table, e.g. "Monday at 8am; Wednesday & Friday at noon", "Daily".
 */
export function describeAutomationTrigger(
  automation: Pick<Automation, "template_id" | "settings">,
): string {
  const { settings } = automation;
  switch (automation.template_id) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      // Coaching goals go out on a fixed schedule (see the template
      // description); it isn't configurable per automation.
      return "Monday at 8am; Wednesday & Friday at noon";
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT: {
      if (!("trigger" in settings)) {
        return "Weekly";
      }
      return describeSchedule(settings.trigger);
    }
    case AutomationTemplateIds.LEAD_BURNING: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return settings.trigger.frequency === "hourly" ? "Hourly" : "Daily";
    }
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.STANDOUT_CALLS:
    case AutomationTemplateIds.PLAYBOOK_FEEDBACK_ALERT:
    case AutomationTemplateIds.CUSTOM: {
      if (!("trigger" in settings)) {
        return "Daily";
      }
      return describeSchedule(settings.trigger);
    }
    default:
      assertUnreachable(automation.template_id);
  }
}

/**
 * The automation's next scheduled run for the info popover. We compute a concrete
 * datetime for Playbook score movement only, matching its pre-unification
 * behavior; the other templates keep their humanized cadence, and disabled
 * automations have no next run. The trigger's `time_of_day` is a wall-clock time in
 * PLATFORM_TIME_ZONE — the zone the scheduler builds its crons in —
 * so the shared calculator evaluates in that zone rather than the viewer's
 * local one; a Pacific admin viewing a 09:00 ET automation then sees the real
 * 09:00 ET run, tagged with the zone abbreviation.
 */
export function describeAutomationNextRun(
  automation: Pick<Automation, "template_id" | "settings" | "is_enabled">,
  now: DateTime = DateTime.local(),
): string {
  if (!automation.is_enabled) {
    return "N/A";
  }

  const { settings } = automation;
  if (
    automation.template_id === AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT &&
    "trigger" in settings &&
    // The two frequencies score movement's form offers; the rest of the shared
    // trigger union has no run day to project forward from.
    (settings.trigger.frequency === "weekly" ||
      settings.trigger.frequency === "monthly")
  ) {
    const next = nextScheduledRunAt(settings.trigger, PLATFORM_TIME_ZONE, now);
    if (next) {
      // ZZZZ appends the zone abbreviation ("EST"/"EDT") so the displayed time
      // reads unambiguously in the scheduler's zone.
      return next.toFormat("MMM d, h:mm a ZZZZ");
    }
  }

  return describeAutomationTrigger(automation);
}

/** Formats how long a run took, e.g. "45 sec" or "3 min". */
export function formatRunDuration(
  startedAt: string,
  finishedAt: string,
): string {
  const duration = DateTime.fromISO(finishedAt).diff(
    DateTime.fromISO(startedAt),
  );
  const seconds = Math.max(0, Math.round(duration.as("seconds")));
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  return `${Math.round(seconds / 60)} min`;
}
